// src/components/PacketsWidget.jsx
// Compact packet data display matching macOS Activity Monitor aesthetic.
// Shows packets in/out and data received/sent in real time.

import { memo, useRef, useEffect, useState } from 'react'
import { useDataStore } from '../stores/dataStore'

function formatBytes(b) {
  if (!b || b === 0)             return '0 B'
  if (b < 1024)                  return `${b} B`
  if (b < 1024 * 1024)          return `${(b / 1024).toFixed(1)} KB`
  if (b < 1024 * 1024 * 1024)   return `${(b / (1024 * 1024)).toFixed(1)} MB`
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function formatPackets(n) {
  if (!n || n === 0) return '0'
  if (n < 1000)      return String(n)
  if (n < 1000000)   return `${(n / 1000).toFixed(1)}K`
  return `${(n / 1000000).toFixed(1)}M`
}

// Dual waveform canvas — blue for in, red for out (like Activity Monitor)
const DualWave = memo(function DualWave({ inHistory, outHistory }) {
  const canvasRef   = useRef(null)
  const animRef     = useRef(null)
  const smoothIn    = useRef(new Float32Array(30))
  const smoothOut   = useRef(new Float32Array(30))
  const targetsIn   = useRef(new Float32Array(30))
  const targetsOut  = useRef(new Float32Array(30))
  const lastFrame   = useRef(0)

  useEffect(() => {
    const pIn  = [...new Array(Math.max(0, 30 - inHistory.length)).fill(0),  ...inHistory].slice(-30)
    const pOut = [...new Array(Math.max(0, 30 - outHistory.length)).fill(0), ...outHistory].slice(-30)
    for (let i = 0; i < 30; i++) {
      targetsIn.current[i]  = pIn[i]  || 0
      targetsOut.current[i] = pOut[i] || 0
    }
  }, [inHistory, outHistory])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width  = canvas.offsetWidth  || 200
    canvas.height = canvas.offsetHeight || 60
    const ctx = canvas.getContext('2d')
    let running = true

    function drawWave(ctx, smoothed, max, H, W, color, fillColor, flip = false) {
      const pts = Array.from(smoothed).map((v, i) => ({
        x: (i / 29) * W,
        y: flip
          ? (v / max) * H * 0.85                    // grows downward from top
          : H - (v / max) * H * 0.85,               // grows upward from bottom
      }))

      // Fill
      ctx.beginPath()
      if (flip) {
        ctx.moveTo(0, 0)
        for (const p of pts) ctx.lineTo(p.x, p.y)
        ctx.lineTo(W, 0)
      } else {
        ctx.moveTo(0, H)
        for (const p of pts) ctx.lineTo(p.x, p.y)
        ctx.lineTo(W, H)
      }
      ctx.closePath()
      ctx.fillStyle = fillColor
      ctx.fill()

      // Line
      ctx.beginPath()
      ctx.moveTo(pts[0].x, pts[0].y)
      for (let i = 1; i < pts.length; i++) {
        const prev = pts[i - 1]
        const curr = pts[i]
        const mx = (prev.x + curr.x) / 2
        ctx.quadraticCurveTo(prev.x, prev.y, mx, (prev.y + curr.y) / 2)
      }
      ctx.strokeStyle = color
      ctx.lineWidth   = 1.2
      ctx.globalAlpha = 0.85
      ctx.stroke()
      ctx.globalAlpha = 1
    }

    function draw(ts) {
      if (!running) return
      if (ts - lastFrame.current < 100) { animRef.current = requestAnimationFrame(draw); return }
      if (document.hidden || !document.hasFocus()) { animRef.current = requestAnimationFrame(draw); return }
      lastFrame.current = ts

      const W = canvas.offsetWidth
      const H = canvas.offsetHeight
      if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H }

      let dirty = false
      for (let i = 0; i < 30; i++) {
        const dIn  = (targetsIn.current[i]  - smoothIn.current[i])  * 0.12
        const dOut = (targetsOut.current[i] - smoothOut.current[i]) * 0.12
        if (Math.abs(dIn) > 0.5 || Math.abs(dOut) > 0.5) dirty = true
        smoothIn.current[i]  += dIn
        smoothOut.current[i] += dOut
      }
      if (!dirty) { animRef.current = requestAnimationFrame(draw); return }

      const maxIn  = Math.max(...smoothIn.current,  1)
      const maxOut = Math.max(...smoothOut.current, 1)
      const globalMax = Math.max(maxIn, maxOut)

      ctx.clearRect(0, 0, W, H)

      // Center divider line
      ctx.beginPath()
      ctx.moveTo(0, H / 2)
      ctx.lineTo(W, H / 2)
      ctx.strokeStyle = 'rgba(255,255,255,0.06)'
      ctx.lineWidth = 1
      ctx.stroke()

      // In — blue, grows from center upward
      const halfH = H / 2
      const ptsIn = Array.from(smoothIn.current).map((v, i) => ({
        x: (i / 29) * W,
        y: halfH - (v / globalMax) * halfH * 0.9,
      }))

      ctx.beginPath()
      ctx.moveTo(0, halfH)
      for (const p of ptsIn) ctx.lineTo(p.x, p.y)
      ctx.lineTo(W, halfH)
      ctx.closePath()
      ctx.fillStyle = 'rgba(77,157,224,0.2)'
      ctx.fill()

      ctx.beginPath()
      ctx.moveTo(ptsIn[0].x, ptsIn[0].y)
      for (let i = 1; i < ptsIn.length; i++) {
        const mx = (ptsIn[i-1].x + ptsIn[i].x) / 2
        ctx.quadraticCurveTo(ptsIn[i-1].x, ptsIn[i-1].y, mx, (ptsIn[i-1].y + ptsIn[i].y) / 2)
      }
      ctx.strokeStyle = '#4d9de0'
      ctx.lineWidth   = 1.2
      ctx.globalAlpha = 0.9
      ctx.stroke()
      ctx.globalAlpha = 1

      // Out — red/coral, grows from center downward
      const ptsOut = Array.from(smoothOut.current).map((v, i) => ({
        x: (i / 29) * W,
        y: halfH + (v / globalMax) * halfH * 0.9,
      }))

      ctx.beginPath()
      ctx.moveTo(0, halfH)
      for (const p of ptsOut) ctx.lineTo(p.x, p.y)
      ctx.lineTo(W, halfH)
      ctx.closePath()
      ctx.fillStyle = 'rgba(239,100,100,0.18)'
      ctx.fill()

      ctx.beginPath()
      ctx.moveTo(ptsOut[0].x, ptsOut[0].y)
      for (let i = 1; i < ptsOut.length; i++) {
        const mx = (ptsOut[i-1].x + ptsOut[i].x) / 2
        ctx.quadraticCurveTo(ptsOut[i-1].x, ptsOut[i-1].y, mx, (ptsOut[i-1].y + ptsOut[i].y) / 2)
      }
      ctx.strokeStyle = '#ef6464'
      ctx.lineWidth   = 1.2
      ctx.globalAlpha = 0.85
      ctx.stroke()
      ctx.globalAlpha = 1

      animRef.current = requestAnimationFrame(draw)
    }

    animRef.current = requestAnimationFrame(draw)
    return () => { running = false; cancelAnimationFrame(animRef.current) }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      width={200}
      height={60}
      style={{ display: 'block', width: '100%', height: 60 }}
    />
  )
})

// ─── Main widget ──────────────────────────────────────────────────────────────
export default function PacketsWidget() {
  const speed   = useDataStore(s => s.speed)
  const history = useDataStore(s => s.speedHistory)
  const metrics = useDataStore(s => s.metrics)

  // Derive packet history from speed history (use bps as proxy if no packet data)
  const dlHistory = history.map(h => h.downloadBps || 0)
  const ulHistory = history.map(h => h.uploadBps   || 0)


  const StatRow = ({ label, value, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
      <span style={{ fontSize: 10, color: 'rgba(220,232,245,0.4)', fontFamily: 'var(--font-mono)' }}>{label}</span>
      <span style={{ fontSize: 11, color: color || 'var(--text-primary)', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{value}</span>
    </div>
  )

  return (
    <div style={{
      borderBottom: '1px solid var(--border-subtle)',
      display: 'grid',
      gridTemplateColumns: '140px 1fr 140px',
      flexShrink: 0,
      background: 'var(--bg-surface)',
    }}>
      {/* Left stats — incoming */}
      <div style={{ padding: '10px 12px', borderRight: '1px solid var(--border-subtle)' }}>
        <div style={{ fontSize: 8, fontFamily: 'var(--font-mono)', color: 'rgba(220,232,245,0.25)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Incoming</div>
        <StatRow label="Data recv/sec"  value={speed.downloadHuman || '0 B/s'} color="#4d9de0" />
      </div>

      {/* Center chart */}
      <div style={{ padding: '8px 10px', borderRight: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 2, background: '#4d9de0', borderRadius: 1 }} />
            <span style={{ fontSize: 8, color: '#4d9de0', fontFamily: 'var(--font-mono)' }}>IN</span>
          </div>
        </div>
        <DualWave inHistory={dlHistory} outHistory={ulHistory} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 8, color: '#ef6464', fontFamily: 'var(--font-mono)' }}>OUT</span>
            <div style={{ width: 8, height: 2, background: '#ef6464', borderRadius: 1 }} />
          </div>
        </div>
      </div>

      {/* Right stats — outgoing */}
      <div style={{ padding: '10px 12px' }}>
        <div style={{ fontSize: 8, fontFamily: 'var(--font-mono)', color: 'rgba(220,232,245,0.25)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Outgoing</div>
        <StatRow label="Data sent/sec"  value={speed.uploadHuman || '0 B/s'} color="#ef6464" />
      </div>
    </div>
  )
}
